import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, FileText, X, Users, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const fmtLongDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
const fmtShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "numeric" });
};

const CATEGORY_LABEL = {
  tuition: "Tuition Fee",
  modular_class: "Modular Class Fee",
  individual_session: "Individual Session",
  uniforms: "Uniform",
  books: "Books / Materials",
  events: "Event Fee",
  others: "Other Charges",
  program_assessment: "Program Assessment",
  retention_fee: "Retention Fee",
};

export default function StatementOfAccountPage() {
  const { selectedBranch, selectedSchool } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  // Owners always have full rights; staff need explicit print permission.
  const canPrint = user?.role === "school_owner" || !!(user?.permissions || {}).statement_of_account_print;

  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  // Freshly-fetched school doc so the SOA always uses the latest saved payment
  // info even if the cached BranchContext copy is stale (e.g. right after the
  // owner edits Landing Settings in another tab).
  const [freshSchool, setFreshSchool] = useState(null);
  const [loading, setLoading] = useState(false);

  // Selection filters
  const [scope, setScope] = useState("section"); // "section" | "student"
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [dueDate, setDueDate] = useState(addDaysISO(todayISO(), 7));
  // Which tuition-plan upcoming installments to render on the SOA card:
  //   "all"     – every unpaid future installment
  //   "nearest" – only the closest single upcoming installment
  //   "none"    – hide upcoming entries; show only overdue/current dues
  const [upcomingMode, setUpcomingMode] = useState("all");
  // "compact" = 4 SOAs per A4 sheet (2×2 grid). "standard" = 1 per A4 page.
  const [printLayout, setPrintLayout] = useState("compact");
  const [printOpen, setPrintOpen] = useState(false);

  const branchId = selectedBranch?.id;

  const load = useCallback(async () => {
    if (!branchId) { setStudents([]); setRecords([]); setSchedules([]); setClasses([]); setSections([]); setFreshSchool(null); return; }
    setLoading(true);
    try {
      const [{ data: s }, { data: r }, { data: c }, { data: sec }, { data: sch }, schoolResp] = await Promise.all([
        api.get("/students", { params: { branch_id: branchId } }),
        api.get("/student-accounts", { params: { branch_id: branchId } }),
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/sections", { params: { branch_id: branchId } }),
        api.get("/tuition-schedules/full", { params: { branch_id: branchId } }),
        selectedSchool?.id ? api.get("/schools/mine").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setStudents(s || []); setRecords(r || []); setClasses(c || []); setSections(sec || []); setSchedules(sch || []);
      const match = (schoolResp?.data || []).find((x) => x.id === selectedSchool?.id) || null;
      setFreshSchool(match);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, selectedSchool?.id]);

  useEffect(() => { load(); }, [load]);

  const classOptions = useMemo(
    () => classes.filter((c) => c.class_type !== "individual").sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [classes],
  );
  const sectionOptions = useMemo(
    () => sections.filter((s) => !classFilter || s.class_id === classFilter)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [sections, classFilter],
  );

  // Students filtered by class/section selection
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (classFilter && s.class_id !== classFilter) return false;
      if (sectionFilter && s.section_id !== sectionFilter) return false;
      return true;
    }).sort((a, b) => {
      const an = (a.full_name || a.name || "").toLowerCase();
      const bn = (b.full_name || b.name || "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [students, classFilter, sectionFilter]);

  // Unpaid records grouped by student
  const unpaidByStudent = useMemo(() => {
    const m = {};
    for (const r of records) {
      const balance = Number(r.amount || 0) - Number(r.paid_amount || 0);
      if (balance <= 0.001) continue;
      if (!r.student_id) continue;
      if (!m[r.student_id]) m[r.student_id] = [];
      m[r.student_id].push(r);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (b.date || b.created_at || "").localeCompare(a.date || a.created_at || ""));
    }
    return m;
  }, [records]);

  // Tuition Plan installments grouped by student — overdue + upcoming (any
  // installment with outstanding balance). Marked so the SOA card can
  // visually distinguish "DUE" vs "UPCOMING" rows.
  const tuitionDuesByStudent = useMemo(() => {
    const m = {};
    for (const sched of schedules) {
      const sid = sched.student_id;
      if (!sid) continue;
      const inst = (sched.installments || []).filter((i) => {
        const bal = Number(i.amount_due || 0) - Number(i.paid_total || 0);
        return bal > 0.001;
      });
      if (inst.length === 0) continue;
      inst.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
      m[sid] = inst;
    }
    return m;
  }, [schedules]);

  // A student is printable if they have unpaid student_accounts entries OR
  // any outstanding tuition-plan installment (overdue or upcoming).
  const hasAnyDues = (sid) =>
    (unpaidByStudent[sid] || []).length > 0 || (tuitionDuesByStudent[sid] || []).length > 0;

  // Apply the "Upcoming Tuition Dues" mode to a student's raw tuition list.
  // Returns the tuition installments that would actually appear on the SOA
  // for the current filter + due-date. Reused by the preview list, the
  // per-student picker, the printable-students filter, and the print portal.
  const dueDateForCompare = useMemo(() => {
    const d = new Date(`${dueDate || todayISO()}T00:00:00`);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [dueDate]);

  const visibleTuitionFor = useCallback((sid) => {
    const raw = tuitionDuesByStudent[sid] || [];
    const overdue = raw.filter((i) => new Date(`${i.due_date}T00:00:00`) <= dueDateForCompare);
    let upcoming = raw.filter((i) => new Date(`${i.due_date}T00:00:00`) > dueDateForCompare);
    if (upcomingMode === "none") upcoming = [];
    else if (upcomingMode === "nearest") upcoming = upcoming.slice(0, 1);
    return { overdue, upcoming, all: [...overdue, ...upcoming] };
  }, [tuitionDuesByStudent, dueDateForCompare, upcomingMode]);

  // Students to print: intersection of filter + any outstanding dues visible
  // under the current upcoming-mode. A student with only upcoming tuition
  // dues is correctly excluded when the user picks "Currently due only".
  const printableStudents = useMemo(() => {
    let pool = filteredStudents;
    if (scope === "student" && selectedStudentIds.size > 0) {
      pool = pool.filter((s) => selectedStudentIds.has(s.id));
    }
    return pool.filter((s) => (unpaidByStudent[s.id] || []).length > 0 || visibleTuitionFor(s.id).all.length > 0);
  }, [filteredStudents, scope, selectedStudentIds, unpaidByStudent, visibleTuitionFor]);

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => setSelectedStudentIds(new Set(filteredStudents.map((s) => s.id)));
  const clearSelection = () => setSelectedStudentIds(new Set());

  const handlePrint = () => {
    if (!dueDate) { toast.error("Set the payment due date first"); return; }
    if (printableStudents.length === 0) { toast.error("No students with outstanding balances match the filter"); return; }
    setPrintOpen(true);
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Link to="/students/accounts" className="text-brand-purple hover:text-brand-purpleHover text-sm font-heading inline-flex items-center gap-1" data-testid="soa-back-link">
                <ArrowLeft className="w-4 h-4" /> Student Accounts
              </Link>
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground mt-1">Statement of Account</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Bulk-print outstanding balances for a whole section or hand-picked students.</p>
          </div>
        </div>

        {(() => {
          const s = freshSchool || selectedSchool;
          const b = selectedBranch;
          // Prefer branch-level payment info; fall back to school-level.
          const acctName = b?.payment_account_name || s?.payment_account_name || "";
          const acctDetails = b?.payment_details_text || s?.payment_details_text || "";
          const missing = !acctName && !acctDetails;
          if (!missing) return null;
          return (
            <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex items-start gap-3" data-testid="soa-missing-bank-alert">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#B45309]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-[#78350F]">Bank account details are missing</p>
                <p className="text-sm text-[#92400E] mt-0.5">
                  Parents won&apos;t see where to pay on the printout. Add branch-level details on the{" "}
                  <Link to="/branches" className="underline font-heading" data-testid="soa-open-branches">
                    Branches page
                  </Link>
                  {" "}(edit this branch → &quot;Statement of Account — Branch Payment Info&quot;), or set school-wide defaults at{" "}
                  <Link to="/landing-settings" className="underline font-heading" data-testid="soa-open-settings">
                    Landing Settings → Statement of Account (Print)
                  </Link>.
                </p>
              </div>
            </div>
          );
        })()}

        {/* Filter + due date bar */}
        <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-4 sm:p-5 space-y-4" data-testid="soa-filter-card">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setScope("section"); clearSelection(); }}
              className={`px-4 py-1.5 rounded-full text-sm font-heading transition ${scope === "section" ? "bg-brand-purple text-white" : "bg-brand-purpleLight/40 text-brand-purple hover:bg-brand-purpleLight"}`}
              data-testid="soa-scope-section"
            >
              By Section
            </button>
            <button
              type="button"
              onClick={() => setScope("student")}
              className={`px-4 py-1.5 rounded-full text-sm font-heading transition ${scope === "student" ? "bg-brand-purple text-white" : "bg-brand-purpleLight/40 text-brand-purple hover:bg-brand-purpleLight"}`}
              data-testid="soa-scope-student"
            >
              By Student
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Class</Label>
              <select
                value={classFilter}
                onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(""); clearSelection(); }}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
                data-testid="soa-class"
              >
                <option value="">All classes</option>
                {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Section</Label>
              <select
                value={sectionFilter}
                onChange={(e) => { setSectionFilter(e.target.value); clearSelection(); }}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm disabled:opacity-50"
                disabled={sectionOptions.length === 0}
                data-testid="soa-section"
              >
                <option value="">All sections</option>
                {sectionOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Payment Due Date <span className="text-[#C62828]">*</span></Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="rounded-2xl border-brand-purple/20 h-11"
                data-testid="soa-due-date"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handlePrint}
                disabled={loading || printableStudents.length === 0 || !canPrint}
                title={canPrint ? undefined : "You don't have permission to print statements"}
                className="w-full h-11 rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white disabled:opacity-50"
                data-testid="soa-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print {printableStudents.length} statement{printableStudents.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>

          <div className="pt-3 border-t border-brand-purple/10 grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-2">Upcoming Tuition Dues</p>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: "all", label: "Include all", hint: "Every unpaid installment" },
                  { id: "nearest", label: "Nearest next only", hint: "Just the next upcoming installment" },
                  { id: "none", label: "Currently due only", hint: "Hide all future installments" },
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-heading cursor-pointer transition border ${
                      upcomingMode === opt.id
                        ? "bg-brand-purple border-brand-purple text-white"
                        : "bg-white border-brand-purple/20 text-brand-inkMuted hover:border-brand-purple/40"
                    }`}
                    data-testid={`soa-upcoming-${opt.id}`}
                  >
                    <input
                      type="radio"
                      name="soa-upcoming-mode"
                      value={opt.id}
                      checked={upcomingMode === opt.id}
                      onChange={() => setUpcomingMode(opt.id)}
                      className="hidden"
                    />
                    <span>{opt.label}</span>
                    <span className={`text-[10px] normal-case ${upcomingMode === opt.id ? "opacity-80" : "text-brand-inkMuted"}`}>· {opt.hint}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-2">Print Layout</p>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: "compact", label: "Compact", hint: "4 statements per A4" },
                  { id: "standard", label: "Standard", hint: "1 full-page per A4" },
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-heading cursor-pointer transition border ${
                      printLayout === opt.id
                        ? "bg-brand-purple border-brand-purple text-white"
                        : "bg-white border-brand-purple/20 text-brand-inkMuted hover:border-brand-purple/40"
                    }`}
                    data-testid={`soa-layout-${opt.id}`}
                  >
                    <input
                      type="radio"
                      name="soa-print-layout"
                      value={opt.id}
                      checked={printLayout === opt.id}
                      onChange={() => setPrintLayout(opt.id)}
                      className="hidden"
                    />
                    <span>{opt.label}</span>
                    <span className={`text-[10px] normal-case ${printLayout === opt.id ? "opacity-80" : "text-brand-inkMuted"}`}>· {opt.hint}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {scope === "student" && (
            <div className="space-y-2 pt-2 border-t border-brand-purple/10">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted">
                  Select students ({selectedStudentIds.size} selected)
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllFiltered} className="text-xs h-8" data-testid="soa-select-all">Select all filtered</Button>
                  <Button variant="ghost" size="sm" onClick={clearSelection} className="text-xs h-8" data-testid="soa-clear-selection">Clear</Button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-2xl border border-brand-purple/10 divide-y divide-brand-purple/10 bg-[#FAFBFE]">
                {filteredStudents.length === 0 ? (
                  <p className="p-4 text-sm text-brand-inkMuted italic">No students in the current filter.</p>
                ) : filteredStudents.map((s) => {
                  const unpaid = unpaidByStudent[s.id] || [];
                  const vis = visibleTuitionFor(s.id);
                  const tuitionDues = vis.all;
                  const acctTotal = unpaid.reduce((sum, r) => sum + (Number(r.amount || 0) - Number(r.paid_amount || 0)), 0);
                  const tuitionTotal = tuitionDues.reduce((sum, i) => sum + (Number(i.amount_due || 0) - Number(i.paid_total || 0)), 0);
                  const total = acctTotal + tuitionTotal;
                  const hasOutstanding = unpaid.length + tuitionDues.length > 0;
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white ${!hasOutstanding ? "opacity-50" : ""}`}
                      data-testid={`soa-student-row-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudentIds.has(s.id)}
                        onChange={() => toggleStudent(s.id)}
                        className="rounded"
                        disabled={!hasOutstanding}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-sm text-foreground truncate">{s.full_name || s.name}</p>
                        <p className="text-xs text-brand-inkMuted">{[s.class_name, s.section_name].filter(Boolean).join(" · ") || "—"}</p>
                      </div>
                      <span className={`text-xs font-heading tabular-nums ${hasOutstanding ? "text-[#C62828]" : "text-brand-inkMuted"}`}>
                        {hasOutstanding ? `${currency}${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "No dues"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Preview list */}
        <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-4 sm:p-5" data-testid="soa-preview-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-brand-purple" /> {printableStudents.length} statement{printableStudents.length === 1 ? "" : "s"} to print
            </h2>
            <p className="text-xs text-brand-inkMuted">
              <Users className="w-3 h-3 inline mr-1" />
              {filteredStudents.length} student{filteredStudents.length === 1 ? "" : "s"} in filter
            </p>
          </div>
          {loading ? (
            <p className="text-brand-inkMuted text-sm">Loading…</p>
          ) : printableStudents.length === 0 ? (
            <p className="text-brand-inkMuted text-sm italic">Nothing to print. Adjust the class/section filter or check that students have unpaid records.</p>
          ) : (
            <ul className="divide-y divide-brand-purple/10" data-testid="soa-preview-list">
              {printableStudents.map((s) => {
                const unpaid = unpaidByStudent[s.id] || [];
                const vis = visibleTuitionFor(s.id);
                const tuitionDues = vis.all;
                const acctTotal = unpaid.reduce((sum, r) => sum + (Number(r.amount || 0) - Number(r.paid_amount || 0)), 0);
                const tuitionTotal = tuitionDues.reduce((sum, i) => sum + (Number(i.amount_due || 0) - Number(i.paid_total || 0)), 0);
                const total = acctTotal + tuitionTotal;
                return (
                  <li key={s.id} className="flex items-center justify-between py-3 gap-3">
                    <div>
                      <p className="font-heading text-foreground">{s.full_name || s.name}</p>
                      <p className="text-xs text-brand-inkMuted">
                        {[s.class_name, s.section_name].filter(Boolean).join(" · ")}
                        {" · "}
                        {unpaid.length} account · {tuitionDues.length} tuition
                      </p>
                    </div>
                    <span className="text-sm font-heading tabular-nums text-[#C62828]">
                      {currency}{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {printOpen && (
        <SoaPrintPreview
          school={freshSchool || selectedSchool}
          branch={selectedBranch}
          currency={currency}
          dueDate={dueDate}
          students={printableStudents}
          unpaidByStudent={unpaidByStudent}
          tuitionDuesByStudent={tuitionDuesByStudent}
          upcomingMode={upcomingMode}
          printLayout={printLayout}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </>
  );
}

/* ------------------------- Print preview overlay ------------------------- */
function SoaPrintPreview({ school, branch, currency, dueDate, students, unpaidByStudent, tuitionDuesByStudent, upcomingMode = "all", printLayout = "compact", onClose }) {
  const doPrint = () => window.print();
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Prefer branch-level payment info; fall back to school-level so schools
  // that haven't split by branch yet still get their bank details printed.
  const acctName = branch?.payment_account_name || school?.payment_account_name || "";
  const acctDetails = branch?.payment_details_text || school?.payment_details_text || "";

  useEffect(() => {
    document.body.classList.add("soa-printing");
    return () => document.body.classList.remove("soa-printing");
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-slate-100 overflow-y-auto soa-print-scroll" data-testid="soa-print-overlay">
      {/* Toolbar (hidden on print) */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-sm soa-toolbar">
        <div>
          <p className="font-heading font-bold text-foreground">Statement of Account — Print Preview</p>
          <p className="text-xs text-brand-inkMuted">
            {students.length} statement{students.length === 1 ? "" : "s"} · Due {fmtLongDate(dueDate)} · {printLayout === "standard" ? "1 per A4" : "4 per A4"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full h-10" data-testid="soa-close-print"><X className="w-4 h-4 mr-1.5" /> Close</Button>
          <Button onClick={doPrint} className="rounded-full h-10 bg-brand-purple hover:bg-brand-purpleHover text-white px-5" data-testid="soa-do-print">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* Sheet */}
      <div className={`max-w-[900px] mx-auto py-6 px-4 soa-sheet soa-layout-${printLayout}`}>
        {/* Chunk students into pages depending on the print layout. */}
        {(() => {
          const chunkSize = printLayout === "standard" ? 1 : 4;
          const totalPages = Math.ceil(students.length / chunkSize);
          return Array.from({ length: totalPages }).map((_, pageIdx) => {
          const pageStudents = students.slice(pageIdx * chunkSize, pageIdx * chunkSize + chunkSize);
          return (
            <div key={`page-${pageIdx}`} className={`soa-page ${printLayout === "standard" ? "block" : "grid grid-cols-1 sm:grid-cols-2 gap-4"} mb-6 print:mb-0`}>
              {pageStudents.map((s) => {
          const unpaid = unpaidByStudent[s.id] || [];
          const tuitionDues = tuitionDuesByStudent?.[s.id] || [];
          const acctTotal = unpaid.reduce((sum, r) => sum + (Number(r.amount || 0) - Number(r.paid_amount || 0)), 0);
          const dueDateISO = dueDate;
          const dueDateD = new Date(`${dueDateISO}T00:00:00`);
          const overdueTuition = tuitionDues.filter((i) => new Date(`${i.due_date}T00:00:00`) <= dueDateD);
          let upcomingTuition = tuitionDues.filter((i) => new Date(`${i.due_date}T00:00:00`) > dueDateD);
          if (upcomingMode === "none") {
            upcomingTuition = [];
          } else if (upcomingMode === "nearest") {
            upcomingTuition = upcomingTuition.slice(0, 1);
          }
          const shownTuitionCount = overdueTuition.length + upcomingTuition.length;
          const total = acctTotal
            + overdueTuition.reduce((sum, i) => sum + (Number(i.amount_due || 0) - Number(i.paid_total || 0)), 0)
            + upcomingTuition.reduce((sum, i) => sum + (Number(i.amount_due || 0) - Number(i.paid_total || 0)), 0);
          return (
            <div key={s.id} className="soa-card bg-white shadow-sm rounded-2xl p-4 print:shadow-none print:rounded-none print:p-3">
              {/* Header */}
              <div className="flex items-center gap-2 border-b-2 border-slate-800 pb-2">
                {school?.logo_base64 ? (
                  <img src={school.logo_base64} alt="logo" className="w-10 h-10 object-contain rounded" />
                ) : (
                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-[9px]">LOGO</div>
                )}
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading font-extrabold text-[13px] leading-tight text-foreground uppercase truncate">
                    {school?.name || school?.public_display_name || "School"}
                  </h1>
                  {school?.print_tagline && (
                    <p className="text-[10px] italic text-slate-600 mt-0.5 truncate">&ldquo;{school.print_tagline}&rdquo;</p>
                  )}
                  <p className="text-[9px] text-slate-500 mt-0.5 truncate">
                    {[branch?.name, branch?.address].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>

              {/* Level / Student */}
              <div className="mt-2 space-y-0.5 text-[11px]">
                <p><span className="font-heading font-semibold">Name:</span> {s.full_name || s.name}</p>
                <p><span className="font-heading font-semibold">LEVEL:</span> {[s.class_name, s.section_name].filter(Boolean).join(" — ") || "—"}</p>
              </div>

              {/* Title */}
              <p className="text-center font-heading font-bold text-[12px] mt-2 tracking-wide">STATEMENT OF ACCOUNT</p>

              {/* Table */}
              <table className="w-full mt-1 border-collapse text-[11px]">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="border border-slate-800 px-1.5 py-0.5 text-left font-heading w-[70px]">DATE</th>
                    <th className="border border-slate-800 px-1.5 py-0.5 text-left font-heading">PARTICULARS</th>
                    <th className="border border-slate-800 px-1.5 py-0.5 text-right font-heading w-[80px]">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueTuition.map((i) => {
                    const bal = Number(i.amount_due || 0) - Number(i.paid_total || 0);
                    return (
                      <tr key={`due-${i.id}`}>
                        <td className="border border-slate-800 px-1.5 py-0.5 whitespace-nowrap">{fmtShortDate(i.due_date)}</td>
                        <td className="border border-slate-800 px-1.5 py-0.5">
                          Tuition Plan — {i.label || "Installment"}
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-[#FDECEA] text-[#C62828] text-[10px] font-heading uppercase align-middle">Due</span>
                        </td>
                        <td className="border border-slate-800 px-1.5 py-0.5 text-right tabular-nums">{fmt(bal)}</td>
                      </tr>
                    );
                  })}
                  {unpaid.map((r) => {
                    const bal = Number(r.amount || 0) - Number(r.paid_amount || 0);
                    const parts = r.description || CATEGORY_LABEL[r.category] || r.category;
                    return (
                      <tr key={r.id}>
                        <td className="border border-slate-800 px-1.5 py-0.5 whitespace-nowrap">{fmtShortDate(r.date || (r.created_at || "").slice(0, 10))}</td>
                        <td className="border border-slate-800 px-1.5 py-0.5">{parts}</td>
                        <td className="border border-slate-800 px-1.5 py-0.5 text-right tabular-nums">{fmt(bal)}</td>
                      </tr>
                    );
                  })}
                  {upcomingTuition.map((i) => {
                    const bal = Number(i.amount_due || 0) - Number(i.paid_total || 0);
                    return (
                      <tr key={`upc-${i.id}`} className="text-slate-600">
                        <td className="border border-slate-800 px-1.5 py-0.5 whitespace-nowrap">{fmtShortDate(i.due_date)}</td>
                        <td className="border border-slate-800 px-1.5 py-0.5">
                          Tuition Plan — {i.label || "Installment"}
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-heading uppercase align-middle">Upcoming</span>
                        </td>
                        <td className="border border-slate-800 px-1.5 py-0.5 text-right tabular-nums">{fmt(bal)}</td>
                      </tr>
                    );
                  })}
                  {Array.from({ length: Math.max(0, 2 - (unpaid.length + shownTuitionCount)) }).map((_, i) => (
                    <tr key={`empty-${i}`}>
                      <td className="border border-slate-800 px-1.5 py-0.5 h-4">&nbsp;</td>
                      <td className="border border-slate-800 px-1.5 py-0.5">&nbsp;</td>
                      <td className="border border-slate-800 px-1.5 py-0.5">&nbsp;</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="border border-slate-800 px-1.5 py-0.5"></td>
                    <td className="border border-slate-800 px-1.5 py-0.5 font-heading font-bold text-right">Total</td>
                    <td className="border border-slate-800 px-1.5 py-0.5 text-right font-heading font-bold tabular-nums text-[#C62828]">{fmt(total)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Due-date block */}
              <div className="mt-3 text-sm leading-snug">
                <p>Please be reminded of your account, which is due</p>
                <p className="font-heading font-bold text-[#C62828] uppercase">{fmtLongDate(dueDate).toUpperCase()}</p>
                <p className="italic text-slate-600 mt-1">Kindly disregard if payment has been made.</p>
              </div>

              {/* Bank details */}
              {(acctDetails || acctName) && (
                <div className="mt-3 text-xs leading-relaxed">
                  <p className="font-heading font-bold text-foreground">Account Details:</p>
                  {acctName && (
                    <p className="font-heading text-foreground mt-0.5">Account Name: <span className="font-normal">{acctName}</span></p>
                  )}
                  {acctDetails && (
                    <pre className="whitespace-pre-wrap font-sans mt-0.5 text-slate-800">{acctDetails}</pre>
                  )}
                </div>
              )}

              {/* Footer note */}
              <div className="mt-3 text-[10px] italic text-slate-600 border-t border-slate-300 pt-2">
                Note: If you will pay online, please show your proof of payment or send the screenshot to our facebook page.
              </div>
            </div>
          );
              })}
            </div>
          );
        });
        })()}
      </div>

      <style>{`
        /* Hide the vertical scrollbar on the SOA print-preview overlay while
           still allowing wheel/touch scroll. Requested by owner. */
        .soa-print-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        .soa-print-scroll::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
        @media print {
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }
          /* Hide the underlying React app so ONLY the SOA overlay prints.
             Without this, the app's page + the fixed overlay both print,
             which causes the classic "repeated pages" bug. */
          body.soa-printing > *:not([data-testid="soa-print-overlay"]) {
            display: none !important;
          }
          /* Reset the fixed positioning during print. A position:fixed element
             with overflow:auto makes browsers repeat the visible viewport on
             every printed page. Flowing it into normal document flow lets
             pagination work correctly. */
          body.soa-printing [data-testid="soa-print-overlay"] {
            position: static !important;
            inset: auto !important;
            overflow: visible !important;
            background: white !important;
            height: auto !important;
            width: auto !important;
            z-index: auto !important;
          }
          .soa-toolbar { display: none !important; }
          .soa-sheet { max-width: none !important; margin: 0 !important; padding: 0 !important; }

          /* --------- Compact layout: 4 SOAs per A4 (2×2 grid) --------- */
          .soa-layout-compact .soa-page {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            grid-auto-rows: 1fr;
            gap: 4mm !important;
            page-break-after: always;
            break-after: page;
            margin: 0 !important;
            padding: 0 !important;
            height: 100%;
          }
          .soa-layout-compact .soa-page:last-child { page-break-after: auto; break-after: auto; }
          .soa-layout-compact .soa-card {
            break-inside: avoid;
            page-break-inside: avoid;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 8px 10px !important;
            font-size: 9pt;
            line-height: 1.25;
            border: 1px dashed #cbd5e1;
            overflow: hidden;
          }
          .soa-layout-compact .soa-card h1 { font-size: 11pt !important; }
          .soa-layout-compact .soa-card table { font-size: 8pt !important; }
          .soa-layout-compact .soa-card table th,
          .soa-layout-compact .soa-card table td { padding: 2px 4px !important; }
          .soa-layout-compact .soa-card pre { font-size: 8pt !important; }
          .soa-layout-compact { }
          .soa-layout-compact + * { }
          @page { size: A4; margin: 8mm; }

          /* --------- Standard layout: 1 SOA per A4, full page --------- */
          .soa-layout-standard .soa-page {
            display: block !important;
            page-break-after: always;
            break-after: page;
            margin: 0 !important;
            padding: 0 !important;
          }
          .soa-layout-standard .soa-page:last-child { page-break-after: auto; break-after: auto; }
          .soa-layout-standard .soa-card {
            break-inside: avoid;
            page-break-inside: avoid;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 20px 24px !important;
            font-size: 12pt;
            line-height: 1.4;
          }
          .soa-layout-standard .soa-card h1 { font-size: 18pt !important; }
          .soa-layout-standard .soa-card table { font-size: 11pt !important; }
          .soa-layout-standard .soa-card table th,
          .soa-layout-standard .soa-card table td { padding: 6px 8px !important; }
          .soa-layout-standard .soa-card pre { font-size: 11pt !important; }
        }
      `}</style>
    </div>,
    document.body,
  );
}
